import clsx from 'clsx';
import DOMPurify from 'dompurify';
import parse, { Element, type HTMLReactParserOptions } from 'html-react-parser';
import i18n from 'i18n-react';

import { decodeHtmlEntities } from '../../helpers/decodeHtmlEntities';
import c from './_profileDescription.module.scss';

export interface ProfileDescriptionProps {
  description: string | null;
  description2: string | null;
  description3: string | null;
}

const parserOptions: HTMLReactParserOptions = {
  replace(node) {
    if (node instanceof Element && node.name === 'a') {
      node.attribs.target = '_blank';
      node.attribs.rel = 'noopener noreferrer';
    }
  },
};

// CDATA is stripped before sanitizing since HTML parses it as a comment, which DOMPurify drops with its
// contents. Trimming happens first too, since DOMPurify re-encodes a trailing non-breaking space as &nbsp;.
function sanitizeDescription(html: string | null): string {
  const decoded = decodeHtmlEntities(html)
    .replace(/<!\[CDATA\[|\]\]>/g, '')
    .trim();
  return DOMPurify.sanitize(decoded);
}

export function ProfileDescription({
  description: rawDescription,
  description2: rawDescription2,
  description3: rawDescription3,
}: ProfileDescriptionProps) {
  const description = sanitizeDescription(rawDescription);
  const description2 = decodeHtmlEntities(rawDescription2);
  const description3 = sanitizeDescription(rawDescription3);

  if (!description && !description2 && !description3) return null;

  return (
    <div className={clsx('profile-description', c.wrapper)}>
      {description && (
        <div className={clsx('profile-description__body', c.profileDescriptionBody)}>
          {parse(description, parserOptions)}
        </div>
      )}
      {description2 && (
        <div className={clsx('profile-description__section', c.profileDescriptionSection)}>
          <span className={clsx('profile-description__label', c.profileDescriptionLabel)}>
            {i18n.translate('directoryProfileView.experiences')}
          </span>
          <div>{description2}</div>
        </div>
      )}
      {description3 && (
        <div className={clsx('profile-description__section', c.profileDescriptionSection)}>
          {parse(description3, parserOptions)}
        </div>
      )}
    </div>
  );
}
